import { type Episode, type Movie, type Season, type Tv } from '@tmdb-graphql-api/schema/types';
import { CastRole, CrewJob, CrewJobLookup } from '#enums/castAndCrew.ts';
import {
  type EpisodeCastAndCrewData,
  type MovieCastAndCrewData,
  type SeasonCastAndCrewData,
  type TvCastAndCrewData,
} from '../../types.ts';

const getStars = (cast: Movie['cast']) =>
  (cast ?? []).reduce((acc: string[], entry) => {
    if (!entry || acc.length >= 4) {
      return acc;
    }

    acc.push(entry.name);
    return acc;
  }, []);

const getCrewByJob = (crew: Movie['crew'], job: string) =>
  (crew ?? []).reduce((acc: string[], entry) => {
    if (!entry) {
      return acc;
    }

    if (entry.job === job) {
      acc.push(entry.name);
    }

    return acc;
  }, []);

const formatEpisodeCastAndCrew = (cast: Episode['cast'], crew: Episode['crew'], guestStars: Episode['guestStars']) => {
  const stars = getStars(cast);
  const topGuestStars = getStars(guestStars);
  const director = getCrewByJob(crew, CrewJobLookup.Director);
  const writer = getCrewByJob(crew, CrewJobLookup.Writer);
  const highlights: [CastRole | CrewJob, string][] = [];

  if (stars.length > 0) {
    highlights.push([CastRole.Star, stars.join(', ')]);
  }

  if (topGuestStars.length > 0) {
    highlights.push([CastRole.GuestStar, topGuestStars.join(', ')]);
  }

  if (director.length > 0) {
    highlights.push([CrewJob.Director, director.join(', ')]);
  }

  if (writer.length > 0) {
    highlights.push([CrewJob.Writer, writer.join(', ')]);
  }

  return highlights;
};

const formatMovieCastAndCrew = (cast: Movie['cast'], crew: Movie['crew']) => {
  const stars = getStars(cast);
  const director = getCrewByJob(crew, CrewJobLookup.Director);
  const screenplay = getCrewByJob(crew, CrewJobLookup.Screenplay);
  const novel = getCrewByJob(crew, CrewJobLookup.Novel);
  const highlights: [CastRole | CrewJob, string][] = [];

  if (stars.length > 0) {
    highlights.push([CastRole.Star, stars.join(', ')]);
  }

  if (director.length > 0) {
    highlights.push([CrewJob.Director, director.join(', ')]);
  }

  if (screenplay.length > 0) {
    highlights.push([CrewJob.Screenplay, screenplay.join(', ')]);
  }

  if (novel.length > 0) {
    highlights.push([CrewJob.Novel, novel.join(', ')]);
  }

  return highlights;
};

const formatSeasonCastAndCrew = (cast: Season['cast'], crew: Season['crew']) => {
  const stars = getStars(cast);
  const creator = getCrewByJob(crew, CrewJobLookup.Creator);
  const novel = getCrewByJob(crew, CrewJobLookup.Novel);
  const highlights: [CastRole | CrewJob, string][] = [];

  if (stars.length > 0) {
    highlights.push([CastRole.Star, stars.join(', ')]);
  }

  if (creator.length > 0) {
    highlights.push([CrewJob.Creator, creator.join(', ')]);
  }

  if (novel.length > 0) {
    highlights.push([CrewJob.Novel, novel.join(', ')]);
  }

  return highlights;
};

const formatTvCastAndCrew = (cast: Tv['cast'], crew: Tv['crew']) => {
  const stars = getStars(cast);
  const creator = getCrewByJob(crew, CrewJobLookup.Creator);
  const novel = getCrewByJob(crew, CrewJobLookup.Novel);
  const highlights: [CastRole | CrewJob, string][] = [];

  if (stars.length > 0) {
    highlights.push([CastRole.Star, stars.join(', ')]);
  }

  if (creator.length > 0) {
    highlights.push([CrewJob.Creator, creator.join(', ')]);
  }

  if (novel.length > 0) {
    highlights.push([CrewJob.Novel, novel.join(', ')]);
  }

  return highlights;
};

export const formatCastAndCrewHighlights = (
  data: EpisodeCastAndCrewData | MovieCastAndCrewData | SeasonCastAndCrewData | TvCastAndCrewData | null | undefined,
) => {
  if (!data) {
    return [];
  }

  if ('episode' in data) {
    return formatEpisodeCastAndCrew(data.episode.cast, data.episode.crew, data.episode.guestStars);
  }

  if ('movie' in data) {
    return formatMovieCastAndCrew(data.movie.cast, data.movie.crew);
  }

  if ('season' in data) {
    return formatSeasonCastAndCrew(data.season.cast, data.season.crew);
  }

  return formatTvCastAndCrew(data.tv.cast, data.tv.crew);
};
